import * as React from 'react';
import { styled } from '../../../theme';
import { DeveloperInviteModel } from '@journeyapps-platform/lib-journey-core';
import { AccessColumnEvent } from '../../../widgets/table/TableWidget';
import { RowButtonContainer } from '../../../widgets/table/RowButtonWidget';
import { ButtonIconPosition, ButtonType, ButtonWidget } from '../../../widgets/buttons/ButtonWidget';
import { TooltipPosition } from '../../../utils/Tooltips';
import { useNavigate, useParams } from 'react-router-dom';
import { useDialogStore } from '../../../hooks/useDialogService';
import { useAllowed } from '../../../hooks/useAllowed';
import { Routes } from '../../../routes';
import { DeveloperInviteStatus } from '@journeyapps-platform/types-accounts-hub';
import { DevRow } from './DeveloperTableWidget';
import { Meta } from '../manage-integrations/IntegrationsPage';
import { generatePath } from '../../../utils/paths';
import { UserModelAction } from '@journeyapps-platform/cardinal-catalog';

export type DeveloperInviteRowEvent = AccessColumnEvent<DevRow<DeveloperInviteModel>>;

export interface InviteRowWidgetProps {
  event: DeveloperInviteRowEvent;
}

function generatePills(invite: DeveloperInviteModel) {
  const { options } = invite;
  return (
    <S.PillContainer>
      {options.model.status == DeveloperInviteStatus.ACCEPTED && 'External developer'}
      {options.model.status == DeveloperInviteStatus.PENDING && <Meta label="Invite" value="Pending" />}
      {options.model.status == DeveloperInviteStatus.DECLINED && <Meta label="Invite" value="Declined" />}
    </S.PillContainer>
  );
}

export const InviteRowWidget: React.FC<InviteRowWidgetProps> = (props) => {
  const dialogStore = useDialogStore();
  const navigate = useNavigate();
  const params = useParams<{ org_id: string }>();

  const { event } = props;
  const { row } = event;
  const { dev } = row;

  const isAllowedToDeleteInvite = useAllowed({ action: UserModelAction.DELETE, resource: event.row.dev });
  const isAllowedToUpdateInvite = useAllowed({ action: UserModelAction.UPDATE, resource: event.row.dev });

  const deleteInvite = React.useCallback(async () => {
    const ok = await dialogStore.confirmDialog({
      title: 'Please confirm',
      message: `Are you sure you want to remove ${row.cells.email} from this organization? As an external developer they will still have access to their other organizations.`
    });
    if (!ok) {
      return;
    }
    try {
      await row.dev.delete();
    } catch (e) {
      await dialogStore.errorDialog({
        subTitle: 'Could not remove user',
        errorMessage: 'Check the console for more details.'
      });
      console.error(`Failed to remove user invite with id:${row.key}`, e);
    }
  }, [row]);

  return (
    <S.JustifiedRowButtonContainer>
      {generatePills(dev)}
      {event.active && (
        <S.RowButtons>
          <S.DeleteButton
            icon={'trash'}
            disabled={!isAllowedToDeleteInvite}
            tooltip={isAllowedToDeleteInvite ? 'Remove' : 'Not permitted'}
            type={ButtonType.SECONDARY}
            tooltipPos={TooltipPosition.BOTTOM}
            action={deleteInvite}
          />
          <ButtonWidget
            key={`${row.key}-button`}
            disabled={!isAllowedToUpdateInvite || dev.model.status == DeveloperInviteStatus.DECLINED}
            type={ButtonType.SECONDARY}
            icon={'user'}
            iconPosition={ButtonIconPosition.RIGHT}
            label={'Edit'}
            tooltip={isAllowedToUpdateInvite ? 'Edit developer' : 'Not permitted'}
            tooltipPos={TooltipPosition.BOTTOM}
            action={() => {
              navigate(generatePath(Routes.EDIT_INVITE, { ...params, invite_id: row.key }), {
                replace: true
              });
            }}
          />
        </S.RowButtons>
      )}
    </S.JustifiedRowButtonContainer>
  );
};

namespace S {
  export const RowButtons = styled.div`
    display: flex;
    flex-direction: row;
    column-gap: 5px;
  `;

  export const DeleteButton = styled(ButtonWidget)``;

  export const JustifiedRowButtonContainer = styled(RowButtonContainer)`
    width: 300px;
    justify-content: space-between;
    height: 35px;
    column-gap: 5px;
  `;

  export const PillContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
  `;
}
